import {
  FileAddOutlined,
  FileImageOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState, useCallback } from "react";
import style from "../assignmentCorrectionsPreview/assignmentCorrectionsPreview.module.scss";
import styled from "./questionsItemForEssay.module.scss";
import ToolbarSlate from "../_util/toolbarSlate";
import { Editable, Slate, useSlate, withReact } from "slate-react";
import IconSlate from "../_util/iconSlate";
import ButtonSlate from "../_util/buttonSlate";
import HeadingOne from "../icons/headingOne";
import Italic from "../icons/italic";
import UnderLine from "../icons/underLine";
import BoldIcon from "../icons/boldIcon";
import HeadingTwo from "../icons/headingTwo";
import ListNum from "../icons/listNum";
import ListPuce from "../icons/listPuce";
import TextAlignLeft from "../icons/textAlignLeft";
import TextAlignCenter from "../icons/textAlignCenter";
import TextAlignRight from "../icons/textAlignRight";
import BlockQuote from "../icons/blockQuote";
import JustifyIcon from "../icons/justifyIcon";
import { setNotification } from "../../redux/app/app.slice";
import { useDispatch } from "react-redux";
import isHotkey from "is-hotkey";
import { useUploadHandwritingMutation } from "../../services/studentcourseworks.api";
import mammoth from "mammoth";
import pdfToText from "react-pdftotext";

import {
  Transforms,
  createEditor,
  Node,
  Editor as EditorSlate,
  Element as SlateElement,
} from "slate";
import { Button } from "antd";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};
const LIST_TYPES = ["numbered-list", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];

const serialize = (value) => {
  try {
    return value?.map((n) => Node?.string(n)).join("\n");
  } catch (error) {
    return "";
  }
};

const Editor = ({
  onHAndleChange,
  onFormatChange,
  value,
  readOnly,
  allowPaste,
  allowHandWriting,
  allowUploadFile,
  assignmentId,
  indexQuestion,
  isPreview,
  isPracticeMode,
  submissions = [],
  showAttemptNavigation = false,
}) => {
  const dispatch = useDispatch();
  const [wordsCount, setWordsCount] = useState(0);
  const [isLoading, setIsLoading] = useState({
    handwriting: false,
    wordPdf: false,
  });
  const [uploadHandwritingStudentCourseWork] = useUploadHandwritingMutation();
  const [editorValue, setEditorValue] = useState(
    value || [{ type: "paragraph", children: [{ text: "" }] }],
  );
  const [indexSelectedContent, setIndexSelectedContent] = useState(
    submissions?.length,
  );

  const MarkButton = ({ format, icon }) => {
    const editor = useSlate();
    return (
      <ButtonSlate
        active={isMarkActive(editor, format)}
        onMouseDown={(event) => {
          event.preventDefault();
          toggleMark(editor, format);
        }}
      >
        <IconSlate icon={icon} />
      </ButtonSlate>
    );
  };
  const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(
      editor,
      format,
      TEXT_ALIGN_TYPES.includes(format) ? "align" : "type",
    );
    const isList = LIST_TYPES.includes(format);
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !EditorSlate.isEditor(n) &&
        SlateElement.isElement(n) &&
        LIST_TYPES.includes(n.type) &&
        !TEXT_ALIGN_TYPES.includes(format),
      split: true,
    });
    let newProperties;
    if (TEXT_ALIGN_TYPES.includes(format)) {
      newProperties = {
        align: isActive ? undefined : format,
      };
    } else {
      newProperties = {
        type: isActive ? "paragraph" : isList ? "list-item" : format,
      };
    }
    Transforms.setNodes(editor, newProperties);
    if (!isActive && isList) {
      const block = { type: format, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  };
  const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format);
    if (isActive) {
      EditorSlate.removeMark(editor, format);
    } else {
      EditorSlate.addMark(editor, format, true);
    }
  };
  const isBlockActive = (editor, format, blockType = "type") => {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Array.from(
      EditorSlate.nodes(editor, {
        at: EditorSlate.unhangRange(editor, selection),
        match: (n) =>
          !EditorSlate.isEditor(n) &&
          SlateElement.isElement(n) &&
          n[blockType] === format,
      }),
    );
    return !!match;
  };
  const isMarkActive = (editor, format) => {
    const marks = EditorSlate.marks(editor);
    return marks ? marks[format] === true : false;
  };
  const Element = ({ attributes, children, element }) => {
    const style = { textAlign: element.align };
    switch (element.type) {
      case "block-quote":
        return (
          <blockquote style={style} {...attributes}>
            {children}
          </blockquote>
        );
      case "bulleted-list":
        return (
          <ul style={style} {...attributes}>
            {children}
          </ul>
        );
      case "heading-one":
        return (
          <h1 style={style} {...attributes}>
            {children}
          </h1>
        );
      case "heading-two":
        return (
          <h2 style={style} {...attributes}>
            {children}
          </h2>
        );
      case "list-item":
        return (
          <li style={style} {...attributes}>
            {children}
          </li>
        );
      case "numbered-list":
        return (
          <ol style={style} {...attributes}>
            {children}
          </ol>
        );
      case "color":
        return <span style={{ color: element.color }}>{children}</span>;
      default:
        return (
          <p style={style} {...attributes}>
            {children}
          </p>
        );
    }
  };
  const Leaf = ({ attributes, children, leaf }) => {
    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }
    if (leaf.italic) {
      children = <em>{children}</em>;
    }
    if (leaf.underline) {
      children = <u>{children}</u>;
    }
    return <span {...attributes}>{children}</span>;
  };
  const BlockButton = ({ format, icon }) => {
    const editor = useSlate();
    return (
      <ButtonSlate
        active={isBlockActive(
          editor,
          format,
          TEXT_ALIGN_TYPES.includes(format) ? "align" : "type",
        )}
        onMouseDown={(event) => {
          event.preventDefault();
          toggleBlock(editor, format);
        }}
      >
        <IconSlate icon={icon} />
      </ButtonSlate>
    );
  };

  const [editor] = useState(() => withReact(createEditor()));
  const renderElement = useCallback((props) => <Element {...props} />, []);

  useEffect(() => {
    onFormatChange?.(serialize(editorValue));
  }, [editorValue]);

  useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(editorValue)) {
      setEditorValue(value);
      resetEditor(editor, value);
      setWordsCount(countWords(value));
    }
  }, [value]);

  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  const countWords = (nodes) => {
    const text = nodes.map((node) => Node.string(node)).join(" ");
    return text.trim().split(/\s+/).filter(Boolean)?.length;
  };

  const resetEditor = (editor, nodes) => {
    // Clear editor content
    editor.children.forEach(() => {
        Transforms.delete(editor, { at: [0] });
    });

    // Insert new nodes if `nodes` parameter is provided
    if (nodes) {
      Transforms.insertNodes(editor, nodes);
    }
  };

  const handleExtractTextHandwriting = async (event) => {
    const file = event.target.files?.[0];
    const formData = new FormData();
    formData.append("file", file); // `file` is the file you want to upload
    formData.append("studentworkId", assignmentId); // Any other data you want to send

    setIsLoading({ ...isLoading, handwriting: true });
    const { data, error } = await uploadHandwritingStudentCourseWork(formData);
    if (data) {
      setIsLoading({ ...isLoading, handwriting: false });
      dispatch(
        setNotification({
          type: "success",
          message: "Your handwriting upload has been completed successfully.",
        }),
      );
      const message = data;
      const slateContent = [
        { type: "paragraph", children: [{ text: message }] },
      ];
      resetEditor(editor, slateContent);
      setEditorValue(slateContent);
      onHAndleChange?.(slateContent);
      //onFormatChange?.(slateContent);
      setWordsCount(countWords(slateContent));
    }
    if (error) {
      setIsLoading({ ...isLoading, handwriting: false });
      dispatch(setNotification({ type: "error", message: "Upload failed!" }));
      console.error("Upload failed:", error);
    }
  };

  const handleExtractTextFromWordOrPdf = async (event) => {
    const file = event.target.files?.[0];
    const type = file?.type;
    // check if file is word
    if (
      type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      type === "application/msword"
    ) {
      try {
        setIsLoading({ ...isLoading, wordPdf: true });
        extractTextFromWord(file).then((text) => {
          const slateContent = [
            { type: "paragraph", children: [{ text: text }] },
          ];
          console.log("Extracted text www:", text);
          console.log("Updated Slate content www:", slateContent);
          resetEditor(editor, slateContent);
          setEditorValue(slateContent);
          onHAndleChange?.(slateContent);
          onFormatChange?.(slateContent);
          setWordsCount(countWords(slateContent));
          setIsLoading({ ...isLoading, wordPdf: false });
          dispatch(
            setNotification({
              type: "success",
              message: "Your WORD upload has been completed successfully.",
            }),
          );
        });
      } catch (error) {
        setIsLoading({ ...isLoading, wordPdf: false });
        dispatch(
          setNotification({
            type: "error",
            message: "Failed to extract text from Word!",
          }),
        );
        console.error("Failed to extract text from Word:", error);
      }
    } else if (type === "application/pdf") {
      try {
        setIsLoading({ ...isLoading, wordPdf: true });
        let text = await pdfToText(file);
        // check if text start with "Page 1 of"
        if (text.startsWith("Page 1 of")) {
          text = "";
        }
        const slateContent = [
          { type: "paragraph", children: [{ text: text }] },
        ];
        resetEditor(editor, slateContent);
        setEditorValue(slateContent);
        onHAndleChange?.(slateContent);
        onFormatChange?.(slateContent);
        setWordsCount(countWords(slateContent));
        setIsLoading({ ...isLoading, wordPdf: false });
        dispatch(
          setNotification({
            type: "success",
            message: "Your PDF upload has been completed successfully.",
          }),
        );
      } catch (error) {
        setIsLoading({ ...isLoading, wordPdf: false });

        dispatch(
          setNotification({
            type: "error",
            message: "Failed to extract text from PDF!",
          }),
        );
        console.error("Failed to extract text from PDF:", error);
      }
    }
  };

  const extractTextFromWord = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      if (!arrayBuffer) {
        throw new Error("Failed to read file as ArrayBuffer");
      }
      const { value: textContent } = await mammoth.extractRawText({
        arrayBuffer,
      });
      return textContent;
    } catch (error) {
      console.error("Error extracting text from Word file:", error);
      throw error; // Optionally rethrow the error if you want it to be caught by outer try/catch
    }
  };
  console.log('value', value);

  return (
    <div className={styled.container}>
      <div className={style.textareaContainer}>
        <Slate
          editor={editor}
          initialValue={editorValue}
          value={editorValue}
          onChange={(newValue) => {
            setEditorValue(newValue);
            onHAndleChange?.(newValue);
            setWordsCount(countWords(newValue));
          }}
        >
          <ToolbarSlate className={styled.header}>
            <MarkButton disabled={readOnly} format="bold" icon={<BoldIcon />} />
            <MarkButton disabled={readOnly} format="italic" icon={<Italic />} />
            <MarkButton
              disabled={readOnly}
              format="underline"
              icon={<UnderLine />}
            />
            <BlockButton
              disabled={readOnly}
              format="heading-one"
              icon={<HeadingOne />}
            />
            <BlockButton
              disabled={readOnly}
              format="heading-two"
              icon={<HeadingTwo />}
            />
            <BlockButton
              disabled={readOnly}
              format="block-quote"
              icon={<BlockQuote />}
            />
            <BlockButton
              disabled={readOnly}
              format="numbered-list"
              icon={<ListNum />}
            />
            <BlockButton
              disabled={readOnly}
              format="bulleted-list"
              icon={<ListPuce />}
            />
            <BlockButton
              disabled={readOnly}
              format="left"
              icon={<TextAlignLeft />}
            />
            <BlockButton
              disabled={readOnly}
              format="center"
              icon={<TextAlignCenter />}
            />
            <BlockButton
              disabled={readOnly}
              format="right"
              icon={<TextAlignRight />}
            />
            <BlockButton
              disabled={readOnly}
              format="justify"
              icon={<JustifyIcon />}
            />
          </ToolbarSlate>
          <Editable
            className={"slate"}
            style={{ minHeight: "400px", fontSize: "16px" }}
            placeholder="Type your answer ..."
            onPaste={(event) => {
              if (!allowPaste) {
                event.preventDefault();
                dispatch(
                  setNotification({
                    type: "info",
                    message: "Pasting is not allowed in this field.",
                  }),
                );
                return;
              }
            }}
            onKeyDown={(event) => {
              for (const hotkey in HOTKEYS) {
                if (isHotkey(hotkey, event)) {
                  event.preventDefault();
                  const mark = HOTKEYS[hotkey];
                  toggleMark(editor, mark);
                }
              }
            }}
            renderLeaf={renderLeaf}
            renderElement={renderElement}
            readOnly={readOnly}
          />
        </Slate>
      </div>
      <div className="flex align-center flex-between gap-10">
        <div>
          {wordsCount > 0 && (
            <div className={styled.wordsCount}>Words: {wordsCount}</div>
          )}
        </div>

        {(!isPreview || (!isPreview && isPracticeMode)) && (
          <div className={styled.fileContainer}>
            {allowHandWriting && (
              <Button
                className={styled.fileBtn}
                loading={isLoading.handwriting}
              >
                <FileImageOutlined style={{ fontSize: "20px" }} /> image
                <input
                  type="file"
                  className={styled.fileInputHandwriting}
                  name={`myfile-${indexQuestion}`}
                  onChange={handleExtractTextHandwriting}
                />
              </Button>
            )}
            {allowUploadFile && (
              <Button className={styled.fileBtn} loading={isLoading.wordPdf}>
                <FileAddOutlined style={{ fontSize: "20px" }} /> file
                <input
                  type="file"
                  className={styled.fileInputWordPdf}
                  name={`filewordpdf-${indexQuestion}`}
                  onChange={handleExtractTextFromWordOrPdf}
                />
              </Button>
            )}
          </div>
        )}

        <div>
          {showAttemptNavigation && submissions?.length > 0 && (
            <div>
              <Button
                type="text"
                onClick={() => {
                  const slateContent =
                    submissions[indexSelectedContent - 1]
                      ?.studentQuestionAnswers?.[indexQuestion]
                      ?.slateFormatAnswer;

                  resetEditor(editor, slateContent);
                  setIndexSelectedContent((prev) => prev - 1);
                }}
                disabled={indexSelectedContent === 0}
              >
                <LeftOutlined />
              </Button>
              <span>
                {`${indexSelectedContent + 1} / ${
                  submissions?.length + 1
                }`}{" "}
              </span>
              <Button
                type="text"
                onClick={() => {
                  const slateContent =
                    submissions[indexSelectedContent + 1]
                      ?.studentQuestionAnswers?.[indexQuestion]
                      ?.slateFormatAnswer;
                  resetEditor(editor, slateContent);
                  setIndexSelectedContent((prev) => prev + 1);
                }}
                disabled={indexSelectedContent + 1 === submissions?.length + 1}
              >
                <RightOutlined />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Editor;
